import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";

import SiteLayout from "@/components/SiteLayout";
import Icon from "@/components/Icon";
import { useReveal } from "@/hooks/useReveal";
import PhaseStepper from "@/components/PhaseStepper";
import CountUp from "@/components/CountUp";
import {
  EVARA,
  SERVICES,
  INDUSTRIES,
  IMPACT,
  WHY_EVARA,
  PILLARS,
  EXPERTS,
} from "@/data/evara";

const EASE = [0.4, 0, 0.2, 1];

function Reveal({ delay = 0, y = 18, children, className = "" }) {
  const prm = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: EASE, delay: prm ? 0 : delay }}
    >
      {children}
    </motion.div>
  );
}

function Eyebrow({ children }) {
  return (
    <div className="flex items-center gap-3">
      <span className="dot" />
      <span className="eyebrow">{children}</span>
    </div>
  );
}

/* -------- HERO (kept, minor motion polish) -------- */
function Hero() {
  return (
    <section className="relative overflow-hidden bg-sage-gradient bg-hero-watermark" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-20 md:pt-28 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10">
            <div className="fade-up inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card text-xs uppercase tracking-[0.18em] font-semibold" data-testid="hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--evara-sage)]" />
              <span style={{ color: "var(--evara-primary)" }}>Ever Growing Excellence</span>
            </div>
            <h1 className="font-serif fade-up delay-1 mt-7 text-5xl sm:text-6xl lg:text-[5.4rem] leading-[0.98] tracking-tighter text-[var(--evara-text)]">
              Empowering<br />Healthcare<br />
              <span className="inline-block text-[var(--evara-primary)]">Through Excellence.</span>
            </h1>
            <p className="fade-up delay-2 mt-8 text-base md:text-lg text-[var(--evara-text-2)] max-w-2xl leading-relaxed">
              {EVARA.heroSubhead}
            </p>
            <div className="fade-up delay-3 mt-9 flex flex-wrap items-center gap-4">
              <Link to="/services" className="btn-primary text-base" data-testid="hero-services-btn">
                Explore Services <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn-secondary text-base" data-testid="hero-contact-btn">
                Contact Us
              </Link>
            </div>
            <div className="fade-up delay-4 mt-12 flex items-center gap-5">
              <div className="flex -space-x-3">
                <span className="w-9 h-9 rounded-full bg-[var(--evara-mint)] border-2 border-white" />
                <span className="w-9 h-9 rounded-full bg-[var(--evara-sage-2)] border-2 border-white" />
                <span className="w-9 h-9 rounded-full bg-[var(--evara-sage)] border-2 border-white" />
              </div>
              <div className="text-xs text-[var(--evara-text-2)] leading-snug">
                <div className="font-semibold text-[var(--evara-primary)]">From assessment to implementation.</div>
                <div>A senior team. Six-step transformation framework. Measurable outcomes.</div>
              </div>
            </div>
          </div>
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-card p-6">
                <div className="eyebrow">Sectors</div>
                <div className="font-serif text-5xl mt-3 text-[var(--evara-primary)]">6+</div>
                <div className="text-xs text-[var(--evara-text-2)] mt-2 leading-snug">Healthcare verticals, hospitals to startups.</div>
              </div>
              <div className="glass-card p-6 mt-6">
                <div className="eyebrow">Framework</div>
                <div className="font-serif text-5xl mt-3 text-[var(--evara-primary)]">6</div>
                <div className="text-xs text-[var(--evara-text-2)] mt-2 leading-snug">Steps from Analyze to Sustain.</div>
              </div>
              <div className="glass-card p-6">
                <div className="eyebrow">Practices</div>
                <div className="font-serif text-5xl mt-3 text-[var(--evara-primary)]">7</div>
                <div className="text-xs text-[var(--evara-text-2)] mt-2 leading-snug">Modular services. One outcome model.</div>
              </div>
              <div className="glass-card p-6 mt-6">
                <div className="eyebrow">Impact</div>
                <div className="font-serif text-5xl mt-3 text-[var(--evara-primary)]">3&times;</div>
                <div className="text-xs text-[var(--evara-text-2)] mt-2 leading-snug">Faster data-driven decisions.</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* -------- ABOUT — heading line-by-line reveal with color shift on the italic -------- */
function AboutSection() {
  const prm = useReducedMotion();
  const lines = [
    { text: "Healthcare transformation,", accent: false },
    { text: "from", accent: false },
    { text: "assessment to implementation.", accent: true },
  ];
  return (
    <section className="section-pad" data-testid="about-section">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <Eyebrow>About EVARA</Eyebrow>
        </Reveal>
        <div className="grid md:grid-cols-12 gap-12 mt-6 items-start">
          <h2 className="md:col-span-5 font-serif text-3xl md:text-5xl leading-tight text-[var(--evara-text)]">
            {lines.map((l, i) => (
              <motion.span
                key={i}
                className="block"
                initial={prm ? { opacity: 1, y: 0, color: l.accent ? "var(--evara-primary)" : "var(--evara-text)" } : { opacity: 0, y: 14, color: l.accent ? "var(--evara-text)" : "var(--evara-text)" }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  color: l.accent ? "var(--evara-primary)" : "var(--evara-text)",
                }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, ease: EASE, delay: prm ? 0 : i * 0.08 }}
                style={l.accent ? { fontStyle: "normal", fontWeight: 500 } : undefined}
              >
                {l.text}
              </motion.span>
            ))}
          </h2>
          <Reveal delay={0.35} className="md:col-span-7">
            <p className="text-[var(--evara-text-2)] leading-relaxed text-base md:text-lg">
              {EVARA.about}
            </p>
            <p className="mt-5 text-[var(--evara-text-2)] leading-relaxed">
              We turn data, training and quality initiatives into measurable, sustainable improvement, partnering with healthcare leaders from baseline assessment through implementation and culture change.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <Link to="/about" className="btn-secondary" data-testid="about-more-btn">
                More about EVARA <ArrowRight size={16} />
              </Link>
              <Link to="/framework" className="hairline-link text-sm font-semibold uppercase tracking-[0.14em] self-center" data-testid="about-framework-link">
                See the framework
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------- FRAMEWORK TEASER — uses shared PhaseStepper -------- */
function FrameworkTimeline() {
  return (
    <section className="section-pad bg-soft-sage" data-testid="framework-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            <Reveal><Eyebrow>Transformation Framework</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif text-3xl md:text-5xl mt-5 leading-tight">
                Six structured steps. <em className="not-italic text-[var(--evara-primary)] font-medium">from data to durable change.</em>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <p className="text-[var(--evara-text-2)] leading-relaxed">
                EVARA&rsquo;s six-phase methodology converts healthcare data into measurable, sustained transformation, across people, process and patient experience.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="rounded-3xl border border-[var(--evara-border)] bg-white p-6 md:p-10">
          <PhaseStepper
            variant="full"
            showTaglineOnHover
            onSelect={(idx0) => { if (typeof window !== "undefined") window.location.href = `/framework?phase=${idx0 + 1}`; }}
          />
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12 flex justify-center">
            <Link to="/framework" className="btn-secondary" data-testid="framework-detail-btn">
              Explore the full framework <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------- SERVICES — 4/2/1 responsive grid, flagship #07 distinct -------- */
function ServicesSection() {
  const prm = useReducedMotion();
  return (
    <section className="section-pad" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 mb-14 items-end">
          <div className="md:col-span-6">
            <Reveal><Eyebrow>Core Services</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif text-3xl md:text-5xl mt-5 leading-tight">
                Seven practice areas. <em className="not-italic text-[var(--evara-primary)] font-medium">One outcome.</em>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.15}>
              <p className="text-[var(--evara-text-2)] leading-relaxed">
                From operational excellence to leadership development, engage one practice or combine all seven for end-to-end transformation.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => {
            const flagship = Boolean(s.flagship);
            return (
              <motion.div
                key={s.key}
                initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, ease: EASE, delay: i * 0.08 }}
                whileHover={prm ? {} : { y: -4 }}
              >
                <Link
                  to={`/services?practice=${s.key}`}
                  className={
                    "block rounded-2xl border p-7 flex flex-col group h-full transition-colors " +
                    (flagship
                      ? "border-2 border-[var(--evara-primary)] bg-white"
                      : "border-[var(--evara-border)] bg-white hover:border-[var(--evara-border-strong)]")
                  }
                  data-testid={`service-card-${s.key}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="w-11 h-11 rounded-xl bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                      <Icon name={s.icon} size={20} />
                    </span>
                    {flagship && (
                      <span className="inline-flex items-center rounded-full bg-[var(--evara-primary)] text-white px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] font-semibold">
                        Flagship
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-xl mt-5 text-[var(--evara-primary)] leading-snug">{s.title}</h3>
                  <p className="text-sm mt-3 text-[var(--evara-text-2)] leading-relaxed flex-1">{s.blurb}</p>
                  <div className="mt-6 text-xs uppercase tracking-[0.16em] font-semibold text-[var(--evara-primary)] flex items-center gap-2 group-hover:gap-3 transition-all">
                    Explore <ArrowRight size={14} />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------- WHO WE SERVE — interactive sector tiles matching Industries hub -------- */
function WhoWeServe() {
  const prm = useReducedMotion();
  const [hoverKey, setHoverKey] = React.useState(null);
  return (
    <section className="section-pad bg-mint-tint" data-testid="industries-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-6">
            <Reveal><Eyebrow>Who We Serve</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif text-3xl md:text-5xl mt-5 leading-tight">
                Built for healthcare. <em className="not-italic text-[var(--evara-primary)] font-medium">across the spectrum.</em>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.15}>
              <p className="text-[var(--evara-text-2)] leading-relaxed">
                From single-specialty IVF centres to multi-specialty hospital networks and high-growth healthcare startups, we adapt to your clinical, cultural and commercial reality.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {INDUSTRIES.map((ind, i) => {
            const hovered = hoverKey === ind.key;
            return (
              <motion.div
                key={ind.key}
                initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, ease: EASE, delay: i * 0.07 }}
                whileHover={prm ? {} : { y: -4 }}
                onMouseEnter={() => setHoverKey(ind.key)}
                onMouseLeave={() => setHoverKey(null)}
              >
                <Link
                  to={`/industries?sector=${ind.key}`}
                  className="block rounded-2xl border border-[var(--evara-border)] bg-white p-6 flex flex-col h-full hover:border-[var(--evara-border-strong)] transition-colors"
                  data-testid={`industry-card-${ind.key}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                    <Icon name={ind.icon} size={20} />
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl mt-5 text-[var(--evara-primary)]">{ind.name}</h3>

                  {/* Hover preview: first understand line */}
                  <div
                    className="mt-3 text-sm text-[var(--evara-text-2)] leading-relaxed italic transition-all overflow-hidden"
                    style={{ maxHeight: hovered ? 120 : 0, opacity: hovered ? 1 : 0 }}
                    aria-hidden={!hovered}
                  >
                    &ldquo;{ind.understand?.[0] || ind.blurb}&rdquo;
                  </div>
                  {!hovered && (
                    <p className="text-sm mt-3 text-[var(--evara-text-2)] leading-relaxed">
                      {ind.blurb}
                    </p>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------- IMPACT — CountUp stats + delayed quote reveal -------- */
function parseStat(str) {
  // "+38%" → {prefix:"+", value:38, suffix:"%"}
  // "3×" → {value:3, suffix:"×"}
  // "20–40%" | "↑ NABH" → non-numeric
  const m = str.match(/^([+\-])?(\d+(?:\.\d+)?)(.*)$/);
  if (!m) return null;
  return { prefix: m[1] || "", value: parseFloat(m[2]), suffix: m[3] || "" };
}

function ImpactSection() {
  const prm = useReducedMotion();
  return (
    <section className="section-pad" data-testid="impact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            <Reveal><Eyebrow>Business Impact</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif text-3xl md:text-5xl mt-5 leading-tight">
                Measurable outcomes. <em className="not-italic text-[var(--evara-primary)] font-medium">agreed before scope.</em>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <p className="text-[var(--evara-text-2)] leading-relaxed">
                Every EVARA engagement begins with clearly defined KPIs. These are the kinds of impact our methodology consistently produces.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {IMPACT.map((o, i) => {
            const parsed = parseStat(o.stat);
            return (
              <motion.div
                key={o.label}
                className="rounded-2xl border border-[var(--evara-border)] bg-white p-7 flex flex-col"
                initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
                data-testid={`impact-card-${i}`}
              >
                <div className="font-serif text-5xl md:text-6xl text-[var(--evara-primary)] leading-none">
                  {parsed ? (
                    <CountUp value={parsed.value} prefix={parsed.prefix} suffix={parsed.suffix} />
                  ) : (
                    o.stat
                  )}
                </div>
                <div className="mt-5 h-[1px] w-12 bg-[var(--evara-mint)]" />
                <p className="mt-4 text-sm text-[var(--evara-text-2)] leading-relaxed">{o.label}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          className="mt-12 rounded-2xl border border-[var(--evara-border)] bg-[var(--evara-bg-soft)] p-7 md:p-10 flex flex-wrap items-center justify-between gap-6"
          initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.55, ease: EASE, delay: 0.6 }}
        >
          <div className="flex items-start gap-4 max-w-2xl">
            <Quote size={28} className="text-[var(--evara-sage)] shrink-0 mt-1" />
            <p className="font-serif text-xl md:text-2xl text-[var(--evara-text)] leading-snug">
              &ldquo;We do not merely conduct training programs. We analyse, advise, build capabilities and transform.&rdquo;
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.16em] font-semibold text-[var(--evara-primary)]">
            The EVARA Promise
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* -------- WHY EVARA (kept, subtle motion) -------- */
function WhyEvara() {
  const prm = useReducedMotion();
  return (
    <section className="section-pad bg-soft-sage" data-testid="why-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-6">
            <Reveal><Eyebrow>Why EVARA</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif text-3xl md:text-5xl mt-5 leading-tight">
                Six reasons leaders choose us <em className="not-italic text-[var(--evara-primary)] font-medium">again and again.</em>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.15}>
              <p className="text-[var(--evara-text-2)] leading-relaxed">
                EVARA is not a generic management-consulting firm. We bring healthcare-specific expertise, analytical rigor and an implementation mindset to every engagement.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {WHY_EVARA.map((w, i) => (
            <motion.div
              key={w.title}
              className="rounded-2xl border border-[var(--evara-border)] bg-white p-7 flex gap-5 items-start"
              initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, ease: EASE, delay: i * 0.07 }}
              data-testid={`why-card-${i}`}
            >
              <div className="icon-chip shrink-0">
                <Icon name={w.icon} size={20} />
              </div>
              <div>
                <h3 className="font-serif text-xl md:text-2xl text-[var(--evara-primary)]">{w.title}</h3>
                <p className="text-sm mt-2 text-[var(--evara-text-2)] leading-relaxed">{w.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------- VALUE PILLARS strip — minimalist 5-icon row -------- */
function Pillars() {
  const prm = useReducedMotion();
  return (
    <section className="pt-6 pb-6 border-b border-[var(--evara-border)]" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              className="flex flex-col items-start gap-3"
              initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.4, ease: EASE, delay: i * 0.06 }}
              data-testid={`pillar-${p.title.toLowerCase()}`}
            >
              <span className="w-10 h-10 rounded-lg bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                <Icon name={p.icon} size={18} />
              </span>
              <div>
                <div className="font-serif text-lg text-[var(--evara-primary)] leading-tight">
                  {p.title}
                </div>
                <p className="mt-1 text-xs text-[var(--evara-text-2)] leading-snug">
                  {p.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------- CTA BAND — bold contextual question with paired CTAs -------- */
function CTABand() {
  const prm = useReducedMotion();
  return (
    <section className="section-pad pt-16 pb-6" data-testid="cta-band-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="rounded-3xl border border-[var(--evara-border)] bg-[var(--evara-bg-mint)] p-8 md:p-12 grid md:grid-cols-12 gap-8 items-center"
          initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, ease: EASE }}
        >
          <div className="md:col-span-8">
            <div className="eyebrow text-[var(--evara-primary)]">Planning a transformation?</div>
            <h3 className="font-serif text-2xl md:text-4xl mt-3 leading-tight text-[var(--evara-primary)]">
              Whether you&rsquo;re scoping an assessment, preparing for accreditation, or building your first analytics team, we&rsquo;ll meet you where you are.
            </h3>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <Link to="/book" className="btn-primary" data-testid="cta-band-book-btn">
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/framework" className="btn-secondary" data-testid="cta-band-framework-btn">
              See the framework
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* -------- EXPERTS — role-based cards -------- */
function Experts() {
  const prm = useReducedMotion();
  return (
    <section className="section-pad" data-testid="experts-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-6">
            <Reveal><Eyebrow>The team</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif text-3xl md:text-5xl mt-5 leading-tight">
                A senior bench, <em className="not-italic text-[var(--evara-primary)] font-medium">not a junior slide deck.</em>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.15}>
              <p className="text-[var(--evara-text-2)] leading-relaxed">
                Every EVARA engagement is run by senior practitioners with hands-on healthcare experience. No revolving door of analysts, no learning curve on your budget.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {EXPERTS.map((e, i) => (
            <motion.div
              key={e.role}
              className="rounded-2xl border border-[var(--evara-border)] bg-white p-6 flex flex-col"
              initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, ease: EASE, delay: i * 0.08 }}
              whileHover={prm ? {} : { y: -4 }}
              data-testid={`expert-card-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                <Icon name={e.icon} size={20} />
              </div>
              <h3 className="font-serif text-lg mt-5 text-[var(--evara-primary)] leading-snug">{e.role}</h3>
              <p className="text-sm mt-3 text-[var(--evara-text-2)] leading-relaxed">{e.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------- FINAL CTA — calm single-unit reveal + prominent hover scale -------- */
function FinalCTA() {
  const prm = useReducedMotion();
  return (
    <section className="section-pad" data-testid="final-cta">
      <motion.div
        className="max-w-6xl mx-auto rounded-3xl border border-[var(--evara-border)] bg-white p-10 md:p-16 grid md:grid-cols-12 gap-10 items-center"
        initial={prm ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.55, ease: EASE }}
      >
        <div className="md:col-span-8">
          <Eyebrow>Let&rsquo;s talk</Eyebrow>
          <h3 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
            Discuss your transformation goals <em className="not-italic text-[var(--evara-primary)] font-medium">with EVARA.</em>
          </h3>
          <p className="mt-5 text-[var(--evara-text-2)] max-w-2xl">
            A 30-minute discovery call to understand your priorities and identify where EVARA can add the most value. No deck. No obligation.
          </p>
        </div>
        <div className="md:col-span-4 flex md:justify-end">
          <motion.div whileHover={prm ? {} : { scale: 1.02 }} transition={{ duration: 0.2, ease: EASE }}>
            <Link to="/book" className="btn-primary" data-testid="final-cta-book-btn">
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

export default function Home() {
  useReveal();
  return (
    <SiteLayout>
      <Hero />
      <Pillars />
      <AboutSection />
      <FrameworkTimeline />
      <ServicesSection />
      <CTABand />
      <WhoWeServe />
      <ImpactSection />
      <Experts />
      <WhyEvara />
      <FinalCTA />
    </SiteLayout>
  );
}
